import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { EnhancedChatMessage } from "@/types/chat";
import { ServerFlightInfo } from "@/types/flights";
export interface ContentImage {
  url: string;
  isMain?: boolean;
  date?: string;
  artist?: string;
  license?: string;
}

export interface AccommodationResult {
  addressline1: string;
  currency: string;
  dailyRate: number;
  freeWifi: boolean;
  hotelId: number;
  hotelName: string;
  imageURL: string;
  includeBreakfast: boolean;
  landingURL: string;
  latitude: number;
  longitude: number;
  policies: { checkIn: string; checkOut: string };
  reviewCount: number;
  reviewScore: number;
  roomtypeName: string;
  starRating: number;
}

// Base recommendation item with common properties
export interface BaseRecommendationItem {
  id: string;
  itemType: string;
}

// Experience recommendation type
export interface ExperienceRecommendation extends BaseRecommendationItem {
  itemType: "experience";
  name: string;
  description: string;
  types: string[];
  image: {
    isMain: boolean;
    url: string;
    source: string;
  };
}

// Accommodation recommendation type
export interface AccommodationRecommendation extends BaseRecommendationItem {
  itemType: "accommodation";
  name: string;
  roomType: string;
  starRating: number;
  reviewScore: number;
  reviewCount: number;
  currency: string;
  dailyRate: number;
  image: {
    isMain: boolean;
    url: string;
    source: string;
  };
  includeBreakfast: boolean;
  freeWifi: boolean;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  policies: {
    checkIn: string;
    checkOut: string;
  };
  address: string;
}

// Flight recommendation type
export interface FlightRecommendation extends BaseRecommendationItem {
  itemType: "flight";
  price: number;
  currency: string;
  departure: string;
  arrival: string;
  from: string;
  to: string;
  cities: {
    from: string;
    to: string;
  };
  airlines: string[];
  duration: {
    departure: number;
    return: number;
    total: number;
  };
  route: Array<{
    airline: string;
    flight: number;
    from: string;
    to: string;
    times: {
      dep: string;
      arr: string;
    };
  }>;
}

// Union type for all recommendation types
export type RecommendationItem =
  | ExperienceRecommendation
  | FlightRecommendation
  | AccommodationRecommendation;

// Legacy type - kept for backward compatibility
export interface ContentItem {
  name: string;
  description: string;
  types: string[];
  image: {
    isMain: boolean;
    url: string;
    source: string;
  };
  id?: string;
}

export type ToraView = "schedule" | "map";
export type ToraContentType = "recommend" | "build";

interface ToraState {
  // Chat
  chatId: string | null;
  chatMessagesByLocation: Record<string, EnhancedChatMessage[]>;
  // Content data from AI responses
  contentData: ContentItem[];
  // New field to store content data by location
  contentDataByLocation: Record<string, ContentItem[]>;
  // Recommendations by type
  experiences: ExperienceRecommendation[];
  flights: ServerFlightInfo[];
  accommodations: AccommodationRecommendation[];
  // Suggestions from actionable items (legacy)
  suggestions: ContentItem[];
  selectedContent: ContentItem | null;
  currentView: ToraView;
  contentType: ToraContentType | null;
  currentLocationId: string | null;
  showRecommendations: boolean;
  showAccommodations: boolean;
  isLoading: boolean;

  // Actions
  setChatId: (id: string) => void;
  setChatMessages: (
    locationId: string,
    messagesOrUpdater:
      | EnhancedChatMessage[]
      | ((prevMessages: EnhancedChatMessage[]) => EnhancedChatMessage[])
  ) => void;
  setContentData: (data: ContentItem[], locationId: string) => void;
  clearContentDataByLocation: (locationId: string) => void;
  clearContentData: () => void;
  selectContent: (identifier: string) => void;
  clearSelectedContent: () => void;
  setCurrentView: (view: ToraView) => void;
  setContentType: (type: ToraContentType) => void;
  setCurrentLocationId: (locationId: string) => void;
  setShowRecommendations: (show: boolean) => void;
  getContentDataForLocation: (locationId: string) => ContentItem[];
  getChatMessages: (locationId: string) => EnhancedChatMessage[];
  // Actions for experiences
  addExperience: (experience: ExperienceRecommendation) => void;
  clearExperiences: () => void;
  getExperiences: () => ExperienceRecommendation[];
  // Actions for flights
  addFlightRecommendations: (flights: ServerFlightInfo[]) => void;
  clearFlightRecommendations: () => void;
  getFlightRecommendations: () => ServerFlightInfo[];
  // Actions for accommodations
  addAccommodation: (accommodation: AccommodationRecommendation) => void;
  clearAccommodations: () => void;
  getAccommodations: () => AccommodationRecommendation[];
  setShowAccommodations: (show: boolean) => void;
  // Legacy suggestion actions
  addSuggestion: (suggestion: ContentItem) => void;
  clearSuggestions: () => void;
  getSuggestions: () => ContentItem[];
  setIsLoading: (loading: boolean) => void;
  clearAll: () => void;
}

const useToraStore = create<ToraState>()(
  devtools(
    (set, get) => ({
      // Initial state
      chatId: null,
      chatMessagesByLocation: {},
      contentData: [],
      contentDataByLocation: {},
      suggestions: [],
      experiences: [],
      flights: [],
      accommodations: [],
      selectedContent: null,
      currentView: "schedule",
      contentType: null,
      currentLocationId: null,
      showRecommendations: false,
      showAccommodations: false,
      isLoading: false,
      // Actions
      setChatId: (id) => {
        set({ chatId: id });
      },
      setChatMessages: (locationId, messagesOrUpdater) => {
        set((state) => ({
          chatMessagesByLocation: {
            ...state.chatMessagesByLocation,
            [locationId]:
              typeof messagesOrUpdater === "function"
                ? messagesOrUpdater(
                    state.chatMessagesByLocation[locationId] || []
                  )
                : messagesOrUpdater,
          },
        }));
      },
      setContentData: (data, locationId) => {
        set((state) => ({
          contentData: data,
          contentDataByLocation: {
            ...state.contentDataByLocation,
            [locationId]: data,
          },
        }));
      },

      clearContentDataByLocation: (locationId: string) => {
        set((state) => ({
          contentDataByLocation: {
            ...state.contentDataByLocation,
            [locationId]: [],
          },
        }));
      },

      clearContentData: () => {
        set({
          contentData: [],
          selectedContent: null,
          contentDataByLocation: {},
          currentLocationId: null,
        });
      },

      clearSelectedContent: () => {
        set({ selectedContent: null });
      },

      setCurrentView: (view) => {
        set({ currentView: view });
      },

      setContentType: (type) => {
        set({ contentType: type });
      },

      setCurrentLocationId: (locationId) => {
        const storedData = get().contentDataByLocation[locationId] || [];
        set({
          currentLocationId: locationId,
          contentData: storedData,
        });
      },

      setShowRecommendations: (show) => {
        set({ showRecommendations: show });
      },

      getContentDataForLocation: (locationId) => {
        return get().contentDataByLocation[locationId] || [];
      },

      getChatMessages: (locationId) => {
        return get().chatMessagesByLocation[locationId] || [];
      },

      // Experience actions
      addExperience: (experience) => {
        set((state) => {
          const exists = state.experiences.some(
            (item) => item.id === experience.id
          );
          if (exists) return state;

          return {
            experiences: [...state.experiences, experience],
          };
        });
      },

      clearExperiences: () => {
        set({ experiences: [] });
      },

      getExperiences: () => {
        return get().experiences;
      },

      // Flight actions
      addFlightRecommendations: (flights) => {
        set({ flights });
      },

      clearFlightRecommendations: () => {
        set({ flights: [] });
      },

      getFlightRecommendations: () => {
        return get().flights;
      },

      // Accommodation actions
      addAccommodation: (accommodation) => {
        set((state) => {
          const exists = state.accommodations.some(
            (item) => item.id === accommodation.id
          );
          if (exists) return state;
          console.log("Adding accommodation", accommodation);
          return {
            accommodations: [...state.accommodations, accommodation],
          };
        });
      },

      clearAccommodations: () => {
        set({ accommodations: [] });
      },

      getAccommodations: () => {
        return get().accommodations;
      },

      setShowAccommodations: (show) => {
        set({ showAccommodations: show });
      },

      // Legacy actions
      addSuggestion: (suggestion) => {
        set((state) => {
          const exists = state.suggestions.some(
            (item) => item.id === suggestion.id
          );
          if (exists) return state;

          return {
            suggestions: [...state.suggestions, suggestion],
          };
        });
      },

      clearSuggestions: () => {
        set({ suggestions: [] });
      },

      getSuggestions: () => {
        return get().suggestions;
      },

      setIsLoading: (loading) => {
        set({ isLoading: loading });
      },

      clearAll: () => {
        set({
          contentData: [],
          suggestions: [],
          flights: [],
          experiences: [],
          accommodations: [],
        });
      },
    }),
    {
      name: "Tora Store",
    }
  )
);

export default useToraStore;
